import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, Line, ComposedChart, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Recycle, Leaf, AlertTriangle, BadgeCheck, Factory, Download, FileText, Upload, Plus, Wrench, ClipboardList } from "lucide-react";
import { openPdf } from "@/lib/pdf";
import { ddmy } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const GREEN = "#1a7f37";
const CAT_COLORS = ["#1a7f37", "#0284C7", "#8B5CF6", "#F59E0B", "#EC4899", "#0f766e", "#B45309", "#64748b"];
const fmtP = (p) => (p ? new Date(p + "-01T00:00:00").toLocaleDateString("en-GB", { month: "short", year: "2-digit" }) : "—");
const gbp = (v) => `£${Math.round(v || 0).toLocaleString()}`;

const STATUS = {
  Conformant: "bg-emerald-100 text-emerald-700",
  Expiring: "bg-amber-100 text-amber-700",
  "Non-Conformant": "bg-red-100 text-red-700",
  Pending: "bg-slate-100 text-slate-600",
};
const RISK = { Low: "text-emerald-600", Medium: "text-amber-600", High: "text-red-600" };
const SITE_NAME = { UK: "Euro Packaging UK", EU: "Euro Packaging France", NI: "Euro Packaging Las Vegas", EXPORT: "Euro Packaging Export" };
const siteLabel = (id) => (SITE_NAME[id] || id).replace("Euro Packaging ", "");

export default function WasteCompliance() {
  return (
    <div>
      <PageHeader title="Waste & Compliance" icon={Recycle} accent={GREEN}
        subtitle="Packaging waste (EPR) reporting and supplier conformance — recyclability, EPR fees, certifications and audit findings across the group." />
      <Tabs defaultValue="waste">
        <TabsList className="bg-white border border-[#e5e9f0]">
          <TabsTrigger data-testid="tab-waste" value="waste" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#1a7f37]">Packaging Waste (EPR)</TabsTrigger>
          <TabsTrigger data-testid="tab-conformance" value="conformance" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#0284C7]">Supplier Conformance</TabsTrigger>
          <TabsTrigger data-testid="tab-nc" value="nc" className="data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#EF4444]">Non-Conformances</TabsTrigger>
        </TabsList>
        <TabsContent value="waste" className="mt-4"><WastePanel /></TabsContent>
        <TabsContent value="conformance" className="mt-4"><ConformancePanel /></TabsContent>
        <TabsContent value="nc" className="mt-4"><NCPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function WastePanel() {
  const { activeSite } = useSite();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(null);
  const [source, setSource] = useState("seed");

  useEffect(() => {
    api.get("/waste/overview", { params: { site: activeSite, period, source } }).then((r) => {
      setData(r.data);
      if (!period && r.data.period) setPeriod(r.data.period);
    }).catch(() => setData(false));
    /* eslint-disable-next-line */
  }, [activeSite, period, source]);

  const exportUrl = (ext) => {
    const p = new URLSearchParams({ site: activeSite, source });
    if (period) p.set("period", period);
    return `/waste/epr-pack.${ext}?${p.toString()}`;
  };
  const setSrc = (s) => { setSource(s); setPeriod(null); setData(null); };

  if (data === null) return <Loader label="Loading packaging waste…" />;
  if (data === false || !data.categories?.length) return <div className="rounded-xl border border-[#e5e9f0] bg-white p-10 text-center text-sm text-[#6a6d70]">No packaging waste data.</div>;
  const k = data.kpis;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-[#6a6d70]">Reporting period</label>
        <select data-testid="waste-period" value={period || ""} onChange={(e) => setPeriod(e.target.value)}
          className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-1.5 text-sm">
          {data.periods.map((p) => <option key={p} value={p}>{fmtP(p)}</option>)}
        </select>
        <div className="inline-flex overflow-hidden rounded-lg border border-[#e5e9f0]">
          {[["seed", "Baseline"], ["live", "Live volumes"]].map(([v, lbl]) => (
            <button key={v} data-testid={`waste-source-${v}`} onClick={() => setSrc(v)}
              className={`px-3 py-1.5 text-xs font-semibold transition ${source === v ? "bg-[#1a7f37] text-white" : "bg-white text-[#6a6d70] hover:bg-[#eef1f5]"}`}>{lbl}</button>
          ))}
        </div>
        {source === "live" && <span className="text-[11px] text-[#89919a]">Derived from actual production & dispatch volumes (est. unit weights)</span>}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="waste-epr-pdf" onClick={() => openPdf(exportUrl("pdf"))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a7f37] px-3 py-2 text-xs font-semibold text-white hover:bg-[#166a2e]"><FileText size={14} /> EPR Pack (PDF)</button>
          <button data-testid="waste-epr-csv" onClick={() => openPdf(exportUrl("csv"))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#c8cfda] px-3 py-2 text-xs font-semibold text-[#354a5f] hover:border-[#1a7f37]"><Download size={14} /> CSV</button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard testId="waste-kpi-pom" label="Placed on Market" value={`${k.pom_t.toLocaleString()} t`} accent={GREEN} />
        <KpiCard testId="waste-kpi-recy" label="Recyclability" value={`${k.recyclability_pct}%`} accent="#0284C7" />
        <KpiCard testId="waste-kpi-recycled" label="Recycled" value={`${k.recycled_t.toLocaleString()} t`} accent="#0f766e" />
        <KpiCard testId="waste-kpi-landfill" label="To Landfill" value={`${k.landfill_t.toLocaleString()} t`} accent={k.landfill_t ? "#EF4444" : "#10B981"} />
        <KpiCard testId="waste-kpi-fee" label="Est. EPR Fee" value={gbp(k.epr_fee)} accent="#B45309" />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3 rounded-xl border border-[#e5e9f0] bg-white p-5">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Leaf size={15} /> 12-Month Trend (tonnes placed vs recycled)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
              <XAxis dataKey="period" tickFormatter={fmtP} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtP} />
              <Bar dataKey="pom_t" name="Placed on market (t)" fill="#c8e6d0" radius={[3, 3, 0, 0]} />
              <Line dataKey="recycled_t" name="Recycled (t)" stroke={GREEN} strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-2 rounded-xl border border-[#e5e9f0] bg-white p-5">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Factory size={15} /> By Site</h3>
          <div className="space-y-2">
            {data.sites.map((s) => (
              <div key={s.site_id} data-testid={`waste-site-${s.site_id}`} className="flex items-center justify-between rounded-lg border border-[#f0f2f5] px-3 py-2">
                <div><div className="font-semibold text-[#32363a]">{siteLabel(s.site_id)}</div><div className="text-[11px] text-[#6a6d70]">{s.site_id} · {s.recyclability_pct}% recyclable · {gbp(s.epr_fee)} EPR</div></div>
                <div className="font-mono text-sm font-semibold text-[#1a7f37]">{s.pom_t.toLocaleString()} t</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h3 className="mb-2 mt-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Recycle size={15} /> Packaging by Material Category — {fmtP(data.period)}</h3>
      <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
            <tr><th className="px-4 py-2.5">Material Category</th><th className="text-right">Placed (t)</th><th>Recyclability</th><th className="text-right">Recycled (t)</th><th className="text-right">EPR Fee</th></tr>
          </thead>
          <tbody data-testid="waste-cat-table">
            {data.categories.map((c, i) => (
              <tr key={c.category} data-testid={`waste-cat-${i}`} className="border-t border-[#f0f2f5]">
                <td className="px-4 py-2.5"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />{c.category}</td>
                <td className="text-right font-mono">{c.pom_t.toLocaleString()}</td>
                <td className="w-40"><div className="flex items-center gap-2"><div className="h-1.5 flex-1 rounded bg-[#eef1f5]"><div className="h-1.5 rounded" style={{ width: `${c.recyclability_pct}%`, background: GREEN }} /></div><span className="text-[11px] text-[#6a6d70]">{c.recyclability_pct}%</span></div></td>
                <td className="text-right font-mono">{c.recycled_t.toLocaleString()}</td>
                <td className="text-right font-mono text-[#B45309]">{gbp(c.epr_fee)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConformancePanel() {
  const [ov, setOv] = useState(null);
  const [list, setList] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [standard, setStandard] = useState("ALL");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [selVendor, setSelVendor] = useState(null);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    if (!selVendor) return;
    setHist(null);
    api.get(`/conformance/supplier/${encodeURIComponent(selVendor)}`).then((r) => setHist(r.data)).catch(() => setHist(false));
  }, [selVendor]);

  const upload = async (cert, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/conformance/${cert.id}/upload`, fd); toast.success("Certificate uploaded"); setTick((t) => t + 1); }
    catch (e) { toast.error("Upload failed"); }
  };

  useEffect(() => { api.get("/conformance/overview").then((r) => setOv(r.data)).catch(() => setOv(false)); }, []);
  useEffect(() => {
    api.get("/conformance/suppliers", { params: { status, standard, q, page, page_size: 25 } }).then((r) => setList(r.data)).catch(() => {});
  }, [status, standard, q, page, tick]);
  useEffect(() => { setPage(1); }, [status, standard, q]);

  const standards = useMemo(() => (ov?.standards || []).map((s) => s.standard), [ov]);
  if (ov === null) return <Loader label="Loading supplier conformance…" />;
  if (ov === false) return <div className="rounded-xl border border-[#e5e9f0] bg-white p-10 text-center text-sm text-[#6a6d70]">No conformance data.</div>;
  const k = ov.kpis;

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiCard testId="conf-kpi-suppliers" label="Suppliers Tracked" value={k.suppliers} accent="#0284C7" />
        <KpiCard testId="conf-kpi-certs" label="Certifications" value={k.certs} accent="#0f766e" />
        <KpiCard testId="conf-kpi-conformant" label="Conformant" value={`${k.conformance_pct}%`} accent="#10B981" />
        <KpiCard testId="conf-kpi-expiring" label="Expiring <90d" value={k.expiring} accent={k.expiring ? "#F59E0B" : "#10B981"} />
        <KpiCard testId="conf-kpi-nonconf" label="Non-Conformant" value={k.non_conformant} accent={k.non_conformant ? "#EF4444" : "#10B981"} />
        <KpiCard testId="conf-kpi-findings" label="Open Findings" value={k.open_findings} accent={k.open_findings ? "#EF4444" : "#10B981"} />
      </div>

      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><BadgeCheck size={15} /> By Standard</h3>
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {ov.standards.map((s) => (
          <div key={s.standard} data-testid={`conf-std-${s.standard}`} className="rounded-xl border border-[#e5e9f0] bg-white p-3">
            <div className="text-sm font-semibold text-[#32363a]">{s.standard}</div>
            <div className="mt-1 text-[11px] text-[#6a6d70]">{s.total} certs · {s.conformant} conformant{s.at_risk ? ` · ${s.at_risk} at risk` : ""}</div>
            <div className="mt-2 h-1.5 rounded bg-[#eef1f5]"><div className="h-1.5 rounded bg-[#10B981]" style={{ width: `${s.total ? Math.round(s.conformant / s.total * 100) : 0}%` }} /></div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select data-testid="conf-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
          <option value="ALL">All statuses</option>{ov.status_list.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="conf-standard" value={standard} onChange={(e) => setStandard(e.target.value)} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
          <option value="ALL">All standards</option>{standards.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto w-full sm:w-72"><SearchInput testId="conf-search" value={q} onChange={setQ} placeholder="Search supplier or cert no…" /></div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
            <tr><th className="px-4 py-2.5">Supplier</th><th>Standard</th><th>Cert No.</th><th>Expiry</th><th className="text-center">Audit</th><th className="text-center">Findings</th><th className="text-center">Certificate</th><th>Risk</th><th>Status</th></tr>
          </thead>
          <tbody data-testid="conf-table">
            {(list?.items || []).map((r) => (
              <tr key={r.id} data-testid={`conf-row-${r.id}`} className="border-t border-[#f0f2f5] hover:bg-[#f7f8fa]">
                <td className="px-4 py-2.5"><button data-testid={`conf-supplier-${r.vendor_code}`} onClick={() => setSelVendor(r.vendor_code)} className="text-left hover:underline"><div className="font-medium text-[#0284C7]">{r.vendor_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.vendor_code} · {r.country}</div></button></td>
                <td className="text-[13px] text-[#32363a]">{r.standard}</td>
                <td className="font-mono text-[11px] text-[#6a6d70]">{r.cert_no}</td>
                <td className="text-[13px]"><span className={r.days_to_expiry < 0 ? "text-red-600 font-semibold" : r.days_to_expiry < 90 ? "text-amber-600" : "text-[#6a6d70]"}>{ddmy(r.expiry_date)}{r.days_to_expiry < 0 ? " · expired" : r.days_to_expiry < 90 ? ` · ${r.days_to_expiry}d` : ""}</span></td>
                <td className="text-center font-mono">{r.audit_score ?? "—"}</td>
                <td className="text-center">{r.open_findings > 0 ? <span className="inline-flex items-center gap-0.5 text-red-600"><AlertTriangle size={12} /> {r.open_findings}</span> : <span className="text-emerald-600">0</span>}</td>
                <td className="text-center">
                  {r.certificate_path
                    ? <button data-testid={`conf-view-${r.id}`} onClick={() => openPdf(`/conformance/${r.id}/certificate`)} className="inline-flex items-center gap-1 rounded border border-[#c8cfda] px-2 py-0.5 text-[11px] text-[#0284C7] hover:border-[#0284C7]"><FileText size={11} /> View</button>
                    : <label data-testid={`conf-upload-${r.id}`} className="inline-flex cursor-pointer items-center gap-1 rounded border border-dashed border-[#c8cfda] px-2 py-0.5 text-[11px] text-[#6a6d70] hover:border-[#0284C7]"><Upload size={11} /> PDF<input type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(r, e.target.files[0])} /></label>}
                </td>
                <td className={`font-semibold ${RISK[r.risk] || ""}`}>{r.risk}</td>
                <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span></td>
              </tr>
            ))}
            {list && list.items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[#6a6d70]">No certifications match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {list && <Pager testId="conf-pager" page={list.page} pages={list.pages} total={list.total} pageSize={list.page_size} onPage={setPage} />}
      <Dialog open={!!selVendor} onOpenChange={(o) => !o && setSelVendor(null)}>
        <DialogContent className="bg-white text-[#32363a] max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle data-testid="conf-hist-title">{hist?.vendor_name || "Supplier history"}</DialogTitle><DialogDescription className="sr-only">Supplier conformance and non-conformance history</DialogDescription></DialogHeader>
          {!hist ? <Loader /> : hist === false ? <div className="p-6 text-center text-sm text-[#6a6d70]">Could not load history.</div> : <SupplierHistory h={hist} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const NC_SEV = { Critical: "bg-red-100 text-red-700", Major: "bg-amber-100 text-amber-700", Minor: "bg-slate-100 text-slate-600" };
const NC_STAT = { Open: "bg-red-100 text-red-700", "In Progress": "bg-amber-100 text-amber-700", Closed: "bg-emerald-100 text-emerald-700" };

function NCPanel() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_name: "", title: "", severity: "Major", assignee: "", due_date: "" });

  const load = () => api.get("/conformance/nonconformances", { params: { status } }).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const advance = async (nc) => {
    if (nc.status === "Closed") return;
    if (nc.status === "In Progress") {
      const action = window.prompt("Corrective action taken to close this non-conformance:", nc.corrective_action || "");
      if (action === null) return;
      await api.patch(`/conformance/nonconformances/${nc.id}`, { status: "Closed", corrective_action: action });
      toast.success(`${nc.nc_no} closed`);
    } else {
      await api.patch(`/conformance/nonconformances/${nc.id}`, { status: "In Progress" });
      toast.success(`${nc.nc_no} → In Progress`);
    }
    load();
  };
  const create = async () => {
    try {
      await api.post("/conformance/nonconformances", form);
      toast.success("Non-conformance raised");
      setOpen(false); setForm({ vendor_name: "", title: "", severity: "Major", assignee: "", due_date: "" });
      load();
    } catch (e) { toast.error("Failed to raise NC"); }
  };

  if (data === null) return <Loader label="Loading non-conformances…" />;
  if (data === false) return <div className="rounded-xl border border-[#e5e9f0] bg-white p-10 text-center text-sm text-[#6a6d70]">No non-conformance data.</div>;
  const k = data.kpis;

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard testId="nc-kpi-total" label="Total NCs" value={k.total} accent="#EF4444" />
        <KpiCard testId="nc-kpi-open" label="Open" value={k.open} accent={k.open ? "#EF4444" : "#10B981"} />
        <KpiCard testId="nc-kpi-progress" label="In Progress" value={k.in_progress} accent="#F59E0B" />
        <KpiCard testId="nc-kpi-closed" label="Closed" value={k.closed} accent="#10B981" />
        <KpiCard testId="nc-kpi-overdue" label="Overdue" value={k.overdue} accent={k.overdue ? "#EF4444" : "#10B981"} />
      </div>

      <div className="mb-4 flex items-center gap-2">
        <select data-testid="nc-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
          <option value="ALL">All statuses</option><option>Open</option><option>In Progress</option><option>Closed</option>
        </select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="nc-new-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#EF4444] px-4 py-2 text-sm font-semibold text-white hover:bg-[#dc2626]"><Plus size={15} /> Raise Non-Conformance</button>
          </DialogTrigger>
          <DialogContent className="bg-white text-[#32363a]">
            <DialogHeader><DialogTitle className="uppercase">Raise Non-Conformance</DialogTitle><DialogDescription className="sr-only">Record a supplier non-conformance and assign a corrective action owner.</DialogDescription></DialogHeader>
            <div className="grid gap-3">
              <input data-testid="nc-vendor" placeholder="Supplier name" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              <input data-testid="nc-title" placeholder="Finding / title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="nc-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm"><option>Critical</option><option>Major</option><option>Minor</option></select>
                <input data-testid="nc-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              </div>
              <input data-testid="nc-assignee" placeholder="Assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
            </div>
            <DialogFooter>
              <button data-testid="nc-create-submit" onClick={create} disabled={!form.vendor_name || !form.title} className="rounded-lg bg-[#EF4444] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Raise NC</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
            <tr><th className="px-4 py-2.5">NC No.</th><th>Supplier</th><th>Finding</th><th>Severity</th><th>Assignee</th><th>Due</th><th>Status</th><th className="text-right pr-4">Action</th></tr>
          </thead>
          <tbody data-testid="nc-table">
            {data.items.map((r) => (
              <tr key={r.id} data-testid={`nc-row-${r.nc_no}`} className="border-t border-[#f0f2f5] hover:bg-[#f7f8fa]">
                <td className="px-4 py-2.5 font-mono text-xs text-[#EF4444]">{r.nc_no}</td>
                <td><div className="text-[#32363a]">{r.vendor_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.standard || "—"}</div></td>
                <td className="text-[13px] text-[#32363a]">{r.title}</td>
                <td><span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${NC_SEV[r.severity] || ""}`}>{r.severity}</span></td>
                <td className="text-[13px] text-[#6a6d70]">{r.assignee || "—"}</td>
                <td className="text-[13px] text-[#6a6d70]">{r.due_date ? ddmy(r.due_date) : "—"}</td>
                <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${NC_STAT[r.status] || ""}`}>{r.status}</span></td>
                <td className="pr-4 text-right">
                  {r.status !== "Closed"
                    ? <button data-testid={`nc-advance-${r.nc_no}`} onClick={() => advance(r)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#EF4444]"><Wrench size={12} /> {r.status === "In Progress" ? "Close" : "Start"}</button>
                    : <span className="font-mono text-[10px] text-emerald-600">closed</span>}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#6a6d70]">No non-conformances.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupplierHistory({ h }) {
  const s = h.summary;
  return (
    <div>
      <div className="mb-3 text-[11px] text-[#6a6d70]">{h.vendor_code} · {h.country || "—"}</div>
      <div className="mb-4 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
        {[["Certs", s.certs, "#0284C7"], ["Conformant", s.conformant, "#10B981"], ["Expiring", s.expiring, "#F59E0B"], ["Non-Conf", s.non_conformant, "#EF4444"], ["Open NCs", s.open_ncs, "#EF4444"], ["Avg Audit", s.avg_audit, "#0f766e"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border border-[#e5e9f0] p-2"><div className="text-lg font-bold" style={{ color: c }}>{v}</div><div className="text-[10px] uppercase text-[#6a6d70]">{l}</div></div>
        ))}
      </div>
      <div className="mb-1 text-xs font-semibold uppercase text-[#6a6d70]">Certifications</div>
      <div className="mb-4 overflow-hidden rounded-lg border border-[#e5e9f0]">
        <table className="w-full text-sm" data-testid="conf-hist-certs"><tbody>
          {h.certs.map((c) => (
            <tr key={c.id} className="border-t border-[#f0f2f5] first:border-0">
              <td className="px-3 py-1.5">{c.standard}</td>
              <td className="font-mono text-[11px] text-[#6a6d70]">{c.cert_no}</td>
              <td className="text-[12px]">exp {ddmy(c.expiry_date)}</td>
              <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[c.status] || ""}`}>{c.status}</span></td>
            </tr>
          ))}
          {h.certs.length === 0 && <tr><td className="px-3 py-3 text-center text-[#6a6d70]">No certifications</td></tr>}
        </tbody></table>
      </div>
      <div className="mb-1 text-xs font-semibold uppercase text-[#6a6d70]">Non-Conformance History</div>
      <div className="overflow-hidden rounded-lg border border-[#e5e9f0]">
        <table className="w-full text-sm" data-testid="conf-hist-ncs"><tbody>
          {h.ncs.map((n) => (
            <tr key={n.id} className="border-t border-[#f0f2f5] first:border-0">
              <td className="px-3 py-1.5 font-mono text-[11px] text-[#EF4444]">{n.nc_no}</td>
              <td className="text-[12px]">{n.title}</td>
              <td className="text-[12px] text-[#6a6d70]">due {n.due_date ? ddmy(n.due_date) : "—"}</td>
              <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${NC_STAT[n.status] || ""}`}>{n.status}</span></td>
            </tr>
          ))}
          {h.ncs.length === 0 && <tr><td className="px-3 py-3 text-center text-[#6a6d70]">No non-conformances</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}
